//ChatGPT version
// UPDATED: 2026-08-20 — added logging on every auth action so the Redux user
// object (including systemRole) can be traced through the store. This was
// added while debugging why systemRole-gated UI (the AI Cost Governance nav
// link) didn't appear until a refresh — AdminLogin.js wasn't dispatching to
// Redux at all. Logging here catches the *next* version of that class of
// bug immediately: if an action fires but state.user.systemRole doesn't
// change the way you expect, it'll show up in this log, not just in a
// component that silently renders the wrong thing.

const LOG_AUTH = true; // flip to false (or gate on process.env.NODE_ENV) to quiet this in production

const initialState = {
  user: null,
  isAuthenticated: false,
  accountType: null,
  token: null,
  loading: true,
  error: null
};

const getUserFromPayload = (payload) => {
  if (!payload) return null;

  return (
    payload.user ||
    payload.admin ||
    payload.currentUser ||
    payload.data?.user ||
    payload.data?.admin ||
    payload.data?.currentUser ||
    payload
  );
};

const getAccountTypeFromUser = (user) => {
  if (!user || typeof user !== 'object') return null;

  return (
    user.accountType ||
    user.account_type ||
    user.role ||
    user.userType ||
    user.type ||
    null
  );
};

const getAccountTypeFromPayload = (payload) => {
  if (!payload) return null;

  const user = getUserFromPayload(payload);

  return (
    payload.accountType ||
    payload.account_type ||
    payload.role ||
    payload.userType ||
    payload.type ||
    payload.data?.accountType ||
    payload.data?.account_type ||
    payload.data?.role ||
    payload.data?.userType ||
    payload.data?.type ||
    getAccountTypeFromUser(user) ||
    null
  );
};

const getTokenFromPayload = (payload) => {
  if (!payload) return null;

  return (
    payload.token ||
    payload.accessToken ||
    payload.authToken ||
    payload.data?.token ||
    payload.data?.accessToken ||
    payload.data?.authToken ||
    null
  );
};

const normalizeAccountType = (accountType) => {
  if (!accountType) return null;

  const value = String(accountType).trim();
  if (!value) return null;

  // Keep your existing display/value convention while making admin checks stable.
  return value.toLowerCase() === 'admin' ? 'Admin' : value;
};

const buildAuthenticatedState = (state, payload) => {
  const user = getUserFromPayload(payload);
  const accountType = normalizeAccountType(getAccountTypeFromPayload(payload));
  const token = getTokenFromPayload(payload);

  return {
    ...state,
    user,
    isAuthenticated: Boolean(user),
    accountType,
    token: token || state.token || null,
    loading: false,
    error: null
  };
};

// ⭐ Logs the fields that actually drive UI gating (App.js's isAdmin /
// isSystemAdmin, PrivateRoute's admin check) so a broken nav link or a 403
// can be traced back to "what did the reducer actually produce" without
// adding console.logs in five different components.
const logAuthTransition = (actionType, prevState, nextState) => {
  if (!LOG_AUTH) return;
  console.log(`[AuthReducer] ${actionType}`);
  console.log('[AuthReducer]   prev → isAuthenticated:', prevState.isAuthenticated,
    'accountType:', prevState.accountType,
    'systemRole:', prevState.user?.systemRole ?? '(none)');
  console.log('[AuthReducer]   next → isAuthenticated:', nextState.isAuthenticated,
    'accountType:', nextState.accountType,
    'systemRole:', nextState.user?.systemRole ?? '(none)');
  console.log('[AuthReducer]   next.user →', nextState.user);
};

export const authReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'AUTH_LOADING':
      if (LOG_AUTH) console.log('[AuthReducer] AUTH_LOADING');
      return {
        ...state,
        loading: true,
        error: null
      };

    case 'LOGIN_SUCCESS':
    case 'AUTH_SUCCESS':
    case 'USER_LOADED':
    case 'LOAD_USER_SUCCESS':
    case 'ADMIN_LOGIN_SUCCESS': {
      if (LOG_AUTH) console.log(`[AuthReducer] ${action.type} — raw payload →`, action.payload);
      const next = buildAuthenticatedState(state, action.payload);
      logAuthTransition(action.type, state, next);
      return next;
    }

    case 'LOGIN_FAILURE':
    case 'AUTH_FAILURE':
    case 'LOAD_USER_FAILURE':
    case 'ADMIN_LOGIN_FAILURE':
      if (LOG_AUTH) console.warn(`[AuthReducer] ${action.type} — error:`, action.payload);
      return {
        ...initialState,
        loading: false,
        error: action.payload
      };

    case 'LOGOUT':
      if (LOG_AUTH) console.log('[AuthReducer] LOGOUT — clearing auth state (had user:', state.user?.email || state.user?.id || '(none)', ')');
      return {
        ...initialState,
        loading: false
      };

    default:
      return state;
  }
};